/**
 * EmployeeAvatar.tsx
 * Polished avatar circle + name cell content for the Skill Matrix employee column.
 *
 * Shows the person's Microsoft Graph photo when available, falling back to
 * deterministic colored initials derived from their display name / email.
 *
 * Pure CSS — no extra state, no network calls (photos are passed in as props).
 * Exported helpers (getInitials, nameToHue) are unit-tested independently.
 */

import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// ── Pure helper functions (exported for unit tests) ───────────────────────────

/**
 * Derive display initials from a full name string.
 * Rules:
 *   - Two-word+ name → first letter of first word + first letter of last word.
 *   - Single word → first letter only.
 *   - Empty / whitespace → "?".
 * Always returns uppercase.
 */
export function getInitials(displayName: string): string {
  const trimmed = displayName.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (
    parts[0].charAt(0).toUpperCase() +
    parts[parts.length - 1].charAt(0).toUpperCase()
  );
}

/**
 * Convert a string (email or name) to a deterministic HSL hue in [0, 360).
 * Uses a simple djb2-style hash so the same input always yields the same hue.
 * The resulting hue is used with fixed saturation/lightness that complements
 * the sage theme — keeping all avatar colors pleasant and consistent.
 */
export function nameToHue(seed: string): number {
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    // djb2: hash = hash * 33 ^ charCode
    hash = ((hash << 5) + hash) ^ seed.charCodeAt(i);
    // Keep within safe integer range by masking to 32-bit signed int
    hash = hash | 0;
  }
  // Map to [0, 360) — use absolute value to handle negatives
  return Math.abs(hash) % 360;
}

/**
 * Build a background color for the initials fallback.
 * Returns an `hsl(...)` CSS string derived from the seed.
 * Saturation 40% / lightness 50% gives readable contrast against white text.
 */
export function avatarBgColor(seed: string): string {
  const hue = nameToHue(seed);
  return `hsl(${hue}, 40%, 50%)`;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface EmployeeAvatarProps {
  displayName: string;
  email: string;
  /** Blob URL from Graph, null when no photo, undefined when not yet fetched. */
  photoUrl?: string | null;
  /** Shown in a small caption when provided. */
  jobClass?: string | null;
  /** Diameter of the avatar circle in pixels. */
  size: number;
}

export const EmployeeAvatar: React.FC<EmployeeAvatarProps> = ({
  displayName,
  email,
  photoUrl,
  size,
}) => {
  const theme = useTheme();
  const initials = getInitials(displayName);
  // Seed on email for stability — display names can change, email is more stable.
  const bgColor = avatarBgColor(email || displayName);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        // Prevent the flex container from growing the row beyond its height.
        overflow: 'hidden',
      }}
    >
      <Avatar
        src={photoUrl ?? undefined}
        sx={{
          width: size,
          height: size,
          fontSize: size <= 24 ? '0.6rem' : size <= 30 ? '0.7rem' : '0.82rem',
          fontWeight: 700,
          flexShrink: 0,
          // Initials fallback background — deterministic per person.
          bgcolor: bgColor,
          color: theme.palette.common.white,
          // White ring so the avatar "pops" off any row background.
          border: `2px solid ${theme.palette.background.paper}`,
          boxShadow: theme.shadows[1],
          transition: 'box-shadow 0.15s ease',
          '&:hover': {
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
          },
        }}
      >
        {initials}
      </Avatar>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontSize: '0.8rem',
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
        title={displayName}
      >
        {displayName}
      </Typography>
    </Box>
  );
};
